import { navigate } from '@reach/router';
import { Formik, FormikHelpers } from 'formik';
import React, { useRef, useState } from 'react';
import { useApiRequest } from '../../../api/useApiRequest';
import { assertNotNull } from '../../../helpers/nullHelpers';
import { RouteComponentProps } from '../../../helpers/RouteComponentProps';
import { urls } from '../../../helpers/urls';
import { BreadcrumbBar, BreadcrumbLink, BreadcrumbSeparator } from '../../../layout/Breadcrumbs';
import { PageContainer } from '../../../layout/Layout';
import { FormErrorBox } from '../../../shared/form/Form';
import { Header1 } from '../../../shared/Headers';
import { Project, ProjectHeader } from '../../projects/project-section/ProjectHeader';
import { projectUrls } from '../../projects/projectUrls';
import { UploadQuoteToNetSuiteModal } from '../UploadQuoteToNetSuiteModal';
import { DataForViewOrEditQuoteBillOfMaterialsForm } from './dataForViewOrEditQuoteBillOfMaterialsForm';
import {
  NextStepAfterSavingBillOfMaterials,
  ViewOrEditQuoteBillOfMaterialsForm,
} from './ViewOrEditQuoteBillOfMaterialsForm';
import {
  ViewOrEditQuoteBillOfMaterialsFormModel,
  EditQuoteBillOfMaterialsFormModelValidator,
  getInitialEditQuoteBillOfMaterialsFormModel,
} from './viewOrEditQuoteBillOfMaterialsFormModel';
import {
  mapEditQuoteBillOfMaterialsFormModelToCommand,
  UpdateQuoteBillOfMaterialsCommand,
} from './updateQuoteBillOfMaterialsCommand';
import { GetRequest } from '../../../shared/GetRequest';

type Props = { project: Project } & RouteComponentProps<{ projectId: string; quoteId: string }>;

export const ViewOrEditQuoteBillOfMaterials = ({ project, projectId, quoteId }: Props) => {
  const getDataRequest = useApiRequest<DataForViewOrEditQuoteBillOfMaterialsForm>({
    httpMethod: 'get',
    endpointUrl: `projects/${projectId}/quotes/${quoteId}/data-for-update-bill-of-materials-form`,
  });

  const updateBillOfMaterialsRequest = useApiRequest<undefined, UpdateQuoteBillOfMaterialsCommand>({
    httpMethod: 'put',
    endpointUrl: `projects/${projectId}/quotes/${quoteId}/bill-of-materials`,
  });

  const [isUploadToNetSuiteModalOpen, setIsUploadToNetSuiteModalOpen] = useState(false);
  const nextStepAfterSave = useRef<NextStepAfterSavingBillOfMaterials>('BackToProject');

  const onSubmit = (
    formModel: ViewOrEditQuoteBillOfMaterialsFormModel,
    formikHelpers: FormikHelpers<ViewOrEditQuoteBillOfMaterialsFormModel>,
  ) => {
    const command = mapEditQuoteBillOfMaterialsFormModelToCommand(
      formModel,
      assertNotNull(getDataRequest.response).quoteId,
    );
    updateBillOfMaterialsRequest.sendRequest({
      requestBody: command,
      onSuccess: () => {
        formikHelpers.setSubmitting(false);

        if (nextStepAfterSave.current === 'UploadToNetSuite') {
          setIsUploadToNetSuiteModalOpen(true);
        } else {
          void navigate(projectUrls.projectDetails(project.projectId));
        }
      },
      onFailure: () => formikHelpers.setSubmitting(false),
    });
  };

  return (
    <GetRequest getDataRequest={getDataRequest}>
      {({ response: data }) => {
        const editBillOfMaterialsFormModelValidator = new EditQuoteBillOfMaterialsFormModelValidator(data.partsById);

        return (
          <>
            <UploadQuoteToNetSuiteModal
              projectId={project.projectId}
              quoteId={data.quoteId}
              isOpen={isUploadToNetSuiteModalOpen}
              closeModal={() => navigate(projectUrls.projectDetails(project.projectId))}
            />
            <BreadcrumbBar>
              <BreadcrumbLink to={urls.home}>Projects</BreadcrumbLink>
              <BreadcrumbSeparator />
              <BreadcrumbLink to={projectUrls.projectDetails(project.projectId)}>
                {project.projectReference}
              </BreadcrumbLink>
              <BreadcrumbSeparator />
              <div>{data.quoteName}</div>
              <BreadcrumbSeparator />
              <div>Bill of Materials</div>
            </BreadcrumbBar>
            <ProjectHeader project={project} />
            <PageContainer>
              <Header1>Bill of Materials</Header1>
              <Formik<ViewOrEditQuoteBillOfMaterialsFormModel>
                onSubmit={onSubmit}
                initialValues={getInitialEditQuoteBillOfMaterialsFormModel(data)}
                validate={formModel => editBillOfMaterialsFormModelValidator.validate(formModel)}
              >
                {formProps => (
                  <ViewOrEditQuoteBillOfMaterialsForm
                    {...formProps}
                    projectId={project.projectId}
                    data={data}
                    setNextStepAfterSave={nextStep => {
                      nextStepAfterSave.current = nextStep;
                    }}
                  />
                )}
              </Formik>
              {updateBillOfMaterialsRequest.error && <FormErrorBox error={updateBillOfMaterialsRequest.error} />}
            </PageContainer>
          </>
        );
      }}
    </GetRequest>
  );
};
